import React, { useEffect, useRef, useState } from "react";

/* ════════════════════════════════════════════════════════════════════════
   Shared UI primitives for the Clients dashboard + Client detail redesign.
   All presentational; no data fetching. Reused across the named components.
   ════════════════════════════════════════════════════════════════════════ */

/* ── icon set (single source so every component draws the same glyphs) ──── */
const s = (children, w = 18) => (
  <svg viewBox="0 0 24 24" width={w} height={w} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
export const Icon = {
  users: (w) => s(<><circle cx="9" cy="8" r="3.2" /><path d="M3 21v-1.6a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4V21" /><path d="M16 3.4a3.2 3.2 0 0 1 0 6.2" /><path d="M21 21v-1.6a4 4 0 0 0-3-3.8" /></>, w),
  folder: (w) => s(<path d="M3 7a2 2 0 0 1 2-2h4l2 3h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />, w),
  doc: (w) => s(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></>, w),
  clock: (w) => s(<><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></>, w),
  plus: (w) => s(<path d="M12 5v14M5 12h14" />, w),
  search: (w) => s(<><circle cx="11" cy="11" r="7" /><path d="m21 21-4.35-4.35" /></>, w),
  filter: (w) => s(<polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />, w),
  caret: (w) => s(<path d="m6 9 6 6 6-6" />, w),
  grid: (w) => s(<><rect x="3" y="3" width="7" height="7" rx="1.4" /><rect x="14" y="3" width="7" height="7" rx="1.4" /><rect x="3" y="14" width="7" height="7" rx="1.4" /><rect x="14" y="14" width="7" height="7" rx="1.4" /></>, w),
  list: (w) => s(<><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><circle cx="3.5" cy="6" r="1" /><circle cx="3.5" cy="12" r="1" /><circle cx="3.5" cy="18" r="1" /></>, w),
  kebab: (w) => s(<><circle cx="12" cy="5" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="12" cy="19" r="1.4" /></>, w),
  pin: (w) => s(<><path d="M12 22s-7-7.5-7-13a7 7 0 1 1 14 0c0 5.5-7 13-7 13z" /><circle cx="12" cy="9" r="2.5" /></>, w),
  arrow: (w) => s(<path d="M5 12h14M13 5l7 7-7 7" />, w),
  back: (w) => s(<path d="M19 12H5M12 5l-7 7 7 7" />, w),
  trophy: (w) => s(<><path d="M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0z" /><path d="M7 4H4v2a3 3 0 0 0 3 3M17 4h3v2a3 3 0 0 1-3 3" /></>, w),
  mail: (w) => s(<><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>, w),
  phone: (w) => s(<path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2 4.2 2 2 0 0 1 4 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.4 2.1L8 9.9a16 16 0 0 0 6 6l1.4-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7A2 2 0 0 1 22 16.9z" />, w),
  cal: (w) => s(<><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>, w),
  edit: (w) => s(<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" /></>, w),
  trash: (w) => s(<><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></>, w),
  open: (w) => s(<><path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" /></>, w),
  bookmark: (w) => s(<path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />, w),
  cube: (w) => s(<><path d="M21 7.5 12 2 3 7.5v9L12 22l9-5.5z" /><path d="M3 7.5 12 13l9-5.5M12 22V13" /></>, w),
  euro: (w) => s(<><path d="M18 7a6.5 6.5 0 1 0 0 10" /><path d="M5 10h7M5 14h7" /></>, w),
  receipt: (w) => s(<><path d="M5 3h14v18l-2.5-1.5L14 21l-2-1.5L10 21l-2.5-1.5L5 21z" /><path d="M9 8h6M9 12h6" /></>, w),
  chart: (w) => s(<><path d="M3 3v18h18" /><path d="m7 14 3-3 3 3 5-6" /></>, w),
  up: (w) => s(<path d="M7 14l5-5 5 5" />, w),
  down: (w) => s(<path d="M7 10l5 5 5-5" />, w),
  check: (w) => s(<path d="M20 6 9 17l-5-5" />, w),
  chevL: (w) => s(<path d="m15 18-6-6 6-6" />, w),
  chevR: (w) => s(<path d="m9 18 6-6-6-6" />, w),
};

/* ── colored icon circle ──────────────────────────────────────────────── */
export function IconCircle({ tone = "blue", round = false, size, children }) {
  return <div className={`cx-ic-circle t-${tone}${round ? " is-round" : ""}`} style={size ? { width: size, height: size } : undefined}>{children}</div>;
}

/* ── trend badge ──────────────────────────────────────────────────────── */
export function TrendBadge({ trend }) {
  if (!trend || trend.pct == null) return null;
  const down = trend.dir === "down";
  return (
    <span className={`cx-trend${down ? " is-down" : ""}`}>
      {down ? Icon.down(13) : Icon.up(13)}{Math.abs(trend.pct)}%
    </span>
  );
}

/* ── sparkline ────────────────────────────────────────────────────────── */
export function Sparkline({ points = [], stroke = "#2D7FF9", className = "cx-stat-spark" }) {
  if (!points || !points.length) return null;
  const max = Math.max(...points), min = Math.min(...points), span = max - min || 1;
  const W = 76, H = 30, n = points.length;
  const pts = points.map((v, i) => {
    const x = (i / (n - 1)) * (W - 4) + 2;
    const y = H - 3 - ((v - min) / span) * (H - 6);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg className={className} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
      <polyline fill="none" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" points={pts} />
    </svg>
  );
}

/* ── search input ─────────────────────────────────────────────────────── */
export function SearchInput({ value, onChange, placeholder, testId }) {
  return (
    <div className="cx-search">
      {Icon.search(16)}
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} />
    </div>
  );
}

/* ── dropdown (one- or two-line trigger + menu) ───────────────────────── */
export function Dropdown({ caption, options, value, onPick, align = "right", twoLine = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", h, true);
    return () => document.removeEventListener("click", h, true);
  }, []);
  const cur = options.find((o) => o.key === value) || options[0];
  return (
    <div className="cx-dd" ref={ref}>
      <button className={`cx-dd-btn${twoLine ? " is-2line" : ""}`} type="button" onClick={() => setOpen((v) => !v)}>
        {twoLine ? (
          <span className="cx-dd-stack"><span className="cx-dd-cap">{caption}</span><span className="cx-dd-val">{cur?.label}</span></span>
        ) : (
          <span className="cx-dd-1line">{caption ? `${caption} ` : ""}{cur?.label}</span>
        )}
        {Icon.caret(13)}
      </button>
      {open && (
        <div className={`cx-dd-menu${align === "left" ? " align-left" : ""}`}>
          {options.map((o) => (
            <div key={o.key} className={`cx-dd-item${o.key === value ? " sel" : ""}`} onClick={() => { onPick(o.key); setOpen(false); }}>
              <span>{o.label}</span>{o.key === value && Icon.check(13)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── view toggle (grid / list) ────────────────────────────────────────── */
export function ViewToggle({ value, onChange }) {
  return (
    <div className="cx-viewtg" role="group" aria-label="View mode">
      <button className={`cx-viewtg-btn${value === "grid" ? " active" : ""}`} onClick={() => onChange("grid")} title="Grid view">{Icon.grid(15)}</button>
      <button className={`cx-viewtg-btn${value === "list" ? " active" : ""}`} onClick={() => onChange("list")} title="List view">{Icon.list(15)}</button>
    </div>
  );
}

/* ── kebab action menu ────────────────────────────────────────────────── */
export function ActionMenu({ open, onToggle, items, btnClassName = "cx-cc-menu-btn", label }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onToggle(false); };
    document.addEventListener("click", h, true);
    return () => document.removeEventListener("click", h, true);
  }, [open, onToggle]);
  return (
    <div className="cx-kebab" ref={ref}>
      <button className={btnClassName} title="Actions" onClick={(e) => { e.stopPropagation(); onToggle(!open); }}>
        {label ? <>{label}{Icon.caret(13)}</> : Icon.kebab(15)}
      </button>
      {open && (
        <div className="cx-kebab-menu" onClick={(e) => e.stopPropagation()}>
          {items.map((it, i) => it.sep ? <div key={i} className="cx-kebab-sep" /> : (
            <button key={i} className={`cx-kebab-item${it.danger ? " danger" : ""}`} onClick={(e) => { e.stopPropagation(); onToggle(false); it.onClick(); }}>
              {it.icon}<span>{it.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── empty state ──────────────────────────────────────────────────────── */
export function EmptyState({ icon, title, hint, action }) {
  return (
    <div className="cx-empty">
      {icon && <div className="cx-empty-ic">{icon}</div>}
      {title && <div className="cx-empty-title">{title}</div>}
      {hint && <div className="cx-empty-hint">{hint}</div>}
      {action}
    </div>
  );
}

/* ── small helpers shared by pages ────────────────────────────────────── */
export const initialsOf = (name) => (name || "?").split(/\s+/).map((w) => w[0] || "").join("").slice(0, 2).toUpperCase();
export const money0 = (n) => "€" + Math.round(Number(n || 0)).toLocaleString();
export const HERO_GRADIENT = "linear-gradient(135deg,#2D7FF9,#4facfe)";
export const heroGradientFor = () => HERO_GRADIENT;
